import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Target } from 'lucide-react';
import { useRecomputeSegment, useSegments } from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { SegmentOut, SegmentStatus } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { SegmentStatusBadge } from '@/components/status/StatusBadge';
import { fmtDateTime, fmtNumber, labelize } from '@/lib/format';
import { useTimezone } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import styles from './SegmentsPage.module.css';

type SegmentRow = Record<string, unknown> & {
  id: string;
  name: string;
  granularity: string;
  status: SegmentStatus;
  videos: number;
  computed: string;
  seg: SegmentOut;
};

export function SegmentsPage() {
  const nav = useNavigate();
  const href = useHref();
  const tz = useTimezone();
  const { toast } = useToastStack();
  const segs = useSegments();
  const recompute = useRecomputeSegment();

  const rows = useMemo<SegmentRow[]>(
    () =>
      (segs.data?.pages.flatMap((p) => p.items) ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        granularity: labelize(s.granularity),
        status: s.status,
        videos: s.video_count,
        computed: s.last_computed_at ?? '',
        seg: s,
      })),
    [segs.data],
  );

  const runRecompute = (s: SegmentOut) =>
    recompute
      .mutateAsync(s.id)
      .then(() =>
        toast({
          type: 'success',
          title: 'Recompute queued',
          description: `${s.name} is being rebuilt in the background.`,
        }),
      )
      .catch((e) =>
        toast({ type: 'error', title: 'Could not queue recompute', description: errorMessage(e) }),
      );

  const columns: DataColumn<SegmentRow>[] = [
    {
      key: 'name',
      label: 'Segment',
      render: (_v, r) => (
        <div className={styles.nameCell}>
          <Link to={href(`/segments/${r.id}`)} className={styles.nameLink}>
            {r.name}
          </Link>
          {r.seg.description ? <span className={styles.description}>{r.seg.description}</span> : null}
        </div>
      ),
    },
    { key: 'granularity', label: 'Granularity', width: 108 },
    {
      key: 'rules',
      label: 'Rules',
      sortable: false,
      width: 150,
      render: (_v, r) => (
        <span className="text-secondary">
          {r.seg.rules.include.length} include, {r.seg.rules.exclude.length} exclude, match {r.seg.match_mode}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: (_v, r) => <SegmentStatusBadge status={r.status} />,
    },
    {
      key: 'videos',
      label: 'Members',
      numeric: true,
      width: 150,
      render: (_v, r) => (
        <span>
          {fmtNumber(r.videos)} videos
          {r.seg.granularity === 'moment' ? `, ${fmtNumber(r.seg.moment_count)} moments` : ''}
        </span>
      ),
    },
    {
      key: 'computed',
      label: 'Last computed',
      width: 150,
      render: (_v, r) => <span className="text-secondary">{fmtDateTime(r.seg.last_computed_at, tz)}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: 160,
      render: (_v, r) => (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={recompute.isPending && recompute.variables === r.id}
          onClick={() => void runRecompute(r.seg)}
        >
          <RefreshCw size={16} strokeWidth={1.75} aria-hidden="true" />
          Recompute
        </Button>
      ),
    },
  ];

  return (
    <main className="page">
      <PageHeading
        title="Segments"
        description="Rule-based targeting sets built from moments. Export them to DV360 or use them in overlay campaigns."
        actions={
          <Button type="button" onClick={() => nav(href('/segments/new'))}>
            <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
            New segment
          </Button>
        }
      />

      {segs.isError ? (
        <div className="stack-4">
          <Alert tone="danger" title="We could not load segments">
            {errorMessage(segs.error)}
          </Alert>
          <div>
            <Button type="button" variant="secondary" onClick={() => void segs.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : segs.isLoading ? (
        <Skeleton label="Loading segments" lines={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Target size={24} strokeWidth={1.5} aria-hidden="true" />}
          title="No segments yet"
          description="Create one from rules, or save a filter from Explore."
          action={
            <Button type="button" variant="secondary" onClick={() => nav(href('/segments/new'))}>
              Create segment
            </Button>
          }
        />
      ) : (
        <div className="stack-4">
          <div className="table-scroll">
            <SortableDataTable
              rows={rows}
              columns={columns}
              rowKey="id"
              caption="Segments"
              defaultSort={{ key: 'name', direction: 'asc' }}
              itemName={{ one: 'segment', other: 'segments' }}
            />
          </div>
          {segs.hasNextPage ? (
            <div className={styles.loadMore}>
              <Button
                type="button"
                variant="secondary"
                loading={segs.isFetchingNextPage}
                onClick={() => void segs.fetchNextPage()}
              >
                Load more segments
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </main>
  );
}
